/*
  <ss-slider>: lean CSS scroll-snap slider shared by the ss-* sections (replaces Swiper).

  Markup
    <ss-slider id="Slider-ID" [data-centered] [data-loop] [data-speed="300"] [data-no-drag]>
      <div>slide</div> <div>slide</div> ...        (direct children are the slides)
    </ss-slider>
  Layout (CSS, so the first frame is right before JS runs): set on the element or an ancestor, per media query
    --ss-pv   slides per view (number, may be fractional, e.g. 1.3)
    --ss-gap  space between slides (length, e.g. 10px)
  Controls (anywhere in the document, matched by the slider id)
    [data-ss-prev="Slider-ID"] [data-ss-next="Slider-ID"]   prev / next buttons (get role, tabindex, aria-*)
    [data-ss-current="Slider-ID"] [data-ss-total="Slider-ID"] text counters (1-based current / total)
  State
    slide.is-active            the active slide (Swiper's .swiper-slide-active)
    control.ss-disabled        + aria-disabled="true" at the beginning / end (never with data-loop)
    control.ss-lock            nothing to slide (Swiper's .swiper-button-lock, display:none in ss-slider.css)
  API / events
    el.next(), el.prev(), el.goTo(realIndex, instant), el.index (real index)
    'ss-slider:change' event on the element, detail { index, total }
  Behaviour: buttons + keyboard (Enter/Space on controls), native touch swipe and trackpad, mouse drag
  on desktop (Swiper's simulateTouch), one slide per swipe, animated moves of data-speed ms (instant with
  prefers-reduced-motion), loop by rotating slides (Swiper 11 loopFix style), re-layout on resize,
  multiple instances, and theme-editor re-render (connected/disconnected callbacks).
*/
(() => {
  if (customElements.get('ss-slider')) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  class SSSlider extends HTMLElement {
    connectedCallback() {
      if (this.ac) return;
      this.ac = new AbortController();
      const o = { signal: this.ac.signal };
      this.loop = this.hasAttribute('data-loop');
      this.centered = this.hasAttribute('data-centered');
      this.speed = +(this.dataset.speed || 300);
      this.total = this.children.length;
      [...this.children].forEach((s, i) => {
        s.dataset.ssI = i;
        s.setAttribute('role', 'group');
        s.setAttribute('aria-label', `${i + 1} / ${this.total}`);
      });
      this.cur = -1;
      const id = this.id;
      const q = (k) => (id ? [...document.querySelectorAll(`[data-ss-${k}="${id}"]`)] : []);
      this.prevEls = q('prev');
      this.nextEls = q('next');
      this.curEls = q('current');
      q('total').forEach((e) => (e.textContent = this.total));
      const bind = (els, fn, label) =>
        els.forEach((b) => {
          if (b.tagName !== 'BUTTON') {
            b.setAttribute('role', 'button');
            b.tabIndex = 0;
          }
          b.setAttribute('aria-label', label);
          if (id) b.setAttribute('aria-controls', id);
          b.addEventListener('click', (e) => (e.preventDefault(), fn()), o);
          b.addEventListener('keydown', (e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), fn()), o);
        });
      bind(this.prevEls, () => this.prev(), 'Previous slide');
      bind(this.nextEls, () => this.next(), 'Next slide');

      this.addEventListener('scroll', () => this.raf || (this.raf = requestAnimationFrame(() => ((this.raf = 0), this.sync()))), o);
      this.addEventListener('scrollend', () => this.idle(), o);
      if (!this.hasAttribute('data-no-drag')) this.drag(o);
      this.ro = new ResizeObserver(() => this.layout());
      this.ro.observe(this);
      if (this.firstElementChild) this.ro.observe(this.firstElementChild);
      this.layout();
    }

    disconnectedCallback() {
      this.ac && this.ac.abort();
      this.ro && this.ro.disconnect();
      cancelAnimationFrame(this.anim);
      this.ac = null;
    }

    // ---- geometry: snap positions of the slides, in DOM order (transform independent)
    measure() {
      const W = this.getBoundingClientRect().width;
      const max = this.scrollWidth - this.clientWidth;
      let x = 0;
      this.pos = [...this.children].map((s, i) => {
        const cs = getComputedStyle(s);
        if (!i) x = parseFloat(cs.marginLeft) || 0;
        const w = s.getBoundingClientRect().width / (this.scale(s) || 1);
        const p = this.centered ? x + w / 2 - W / 2 : x;
        x += w + (parseFloat(cs.marginRight) || 0);
        return Math.max(0, Math.min(max, p));
      });
      this.max = max;
      this.locked = this.centered ? this.total < 2 : max <= 1;
    }
    scale(s) {
      const t = getComputedStyle(s).transform;
      return t && t !== 'none' ? new DOMMatrixReadOnly(t).a : 1;
    }
    near(x) {
      let best = 0;
      this.pos.forEach((p, i) => Math.abs(p - x) < Math.abs(this.pos[best] - x) - 0.5 && (best = i));
      return best;
    }
    domIndex() {
      return this.target != null ? this.target : this.near(this.scrollLeft);
    }
    get index() {
      const s = this.children[this.domIndex()];
      return s ? +s.dataset.ssI : 0;
    }

    layout() {
      if (!this.children.length) return;
      const a = this.cur < 0 ? 0 : this.domIndex();
      this.measure();
      if (!this.anim) this.jump(this.pos[a] || 0);
      if (this.cur < 0) this.edgeFix(0, a);
      this.sync(true);
    }

    // instant scroll with snapping suspended (so intermediate states are not re-snapped)
    jump(x) {
      this.classList.add('ss-moving');
      this.scrollLeft = x;
      this.classList.remove('ss-moving');
    }

    // loop: rotate slides so there is room to move in direction dir (-1 / +1); dir 0 = both sides
    edgeFix(dir, a = this.domIndex()) {
      if (!this.loop || this.locked || this.total < 2) return a;
      const last = this.pos.indexOf(this.max) < 0 ? this.pos.length - 1 : this.pos.indexOf(this.max);
      let moved = false;
      if (dir <= 0 && a <= 0) (this.prepend(this.lastElementChild), a++, (moved = true));
      if (dir >= 0 && a >= last) (this.append(this.firstElementChild), a--, (moved = true));
      if (moved) {
        this.measure();
        this.jump(this.pos[a]);
      }
      return a;
    }

    go(a, instant) {
      a = Math.max(0, Math.min(this.pos.length - 1, a));
      const x = this.pos[a];
      cancelAnimationFrame(this.anim);
      this.anim = 0;
      this.target = a;
      this.sync();
      const from = this.scrollLeft;
      if (instant || !this.speed || reduce.matches || Math.abs(x - from) < 1) {
        this.jump(x);
        return this.done();
      }
      this.classList.add('ss-moving');
      const t0 = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - t0) / this.speed);
        this.scrollLeft = from + (x - from) * ease(t);
        if (t < 1) this.anim = requestAnimationFrame(step);
        else this.done();
      };
      this.anim = requestAnimationFrame(step);
    }
    done() {
      this.anim = 0;
      this.classList.remove('ss-moving');
      this.target = null;
      this.idle();
    }
    idle() {
      if (this.anim || this.dragging) return;
      if (this.loop) this.edgeFix(0);
      this.sync();
    }

    step(dir) {
      if (!this.pos || this.locked) return;
      let a = this.domIndex();
      if (this.loop) {
        if (this.anim) this.done();
        a = this.edgeFix(dir, a);
      }
      const x = this.pos[a];
      let t = a;
      if (dir > 0) while (t < this.pos.length - 1 && this.pos[t] <= x + 0.5) t++;
      else while (t > 0 && this.pos[t] >= x - 0.5) t--;
      if (this.pos[t] === x) return;
      if (dir < 0) while (t > 0 && Math.abs(this.pos[t - 1] - this.pos[t]) < 0.5) t--;
      this.go(t);
    }
    next() {
      this.step(1);
    }
    prev() {
      this.step(-1);
    }
    goTo(real, instant) {
      if (!this.pos) return;
      if (this.anim) this.done();
      const a = [...this.children].findIndex((s) => +s.dataset.ssI === +real);
      if (a >= 0) this.go(a, instant);
    }

    sync(force) {
      if (!this.pos) return;
      const a = this.domIndex();
      const x = this.target != null ? this.pos[a] : this.scrollLeft;
      const begin = !this.loop && x <= this.pos[0] + 0.5;
      const end = !this.loop && x >= this.max - 0.5;
      const flag = (els, off) =>
        els.forEach((b) => {
          b.classList.toggle('ss-disabled', off);
          b.setAttribute('aria-disabled', off);
          if (b.tagName !== 'BUTTON') b.tabIndex = off ? -1 : 0;
          b.classList.toggle('ss-lock', this.locked);
        });
      flag(this.prevEls, begin);
      flag(this.nextEls, end);
      const s = this.children[a];
      if (s === this.activeEl && !force) return;
      if (this.activeEl) this.activeEl.classList.remove('is-active');
      this.activeEl = s;
      if (!s) return;
      s.classList.add('is-active');
      const real = +s.dataset.ssI;
      if (real === this.cur) return;
      this.cur = real;
      this.curEls.forEach((e) => (e.textContent = real + 1));
      this.dispatchEvent(new CustomEvent('ss-slider:change', { detail: { index: real, total: this.total } }));
    }

    // mouse drag on desktop (touch and trackpads use native scrolling)
    drag(o) {
      let x0, s0, t0, a0, moved;
      this.addEventListener('dragstart', (e) => e.preventDefault(), o);
      this.addEventListener(
        'pointerdown',
        (e) => {
          if (e.pointerType !== 'mouse' || e.button || this.locked || !this.pos) return;
          if (this.anim) this.done();
          if (this.loop) this.edgeFix(0);
          x0 = e.clientX;
          s0 = this.scrollLeft;
          t0 = performance.now();
          a0 = this.domIndex();
          moved = false;
          this.dragging = true;
          const mo = { signal: this.ac.signal };
          const move = (ev) => {
            const dx = ev.clientX - x0;
            if (!moved && Math.abs(dx) < 5) return;
            if (!moved) ((moved = true), this.classList.add('ss-moving', 'ss-dragging'));
            this.scrollLeft = s0 - dx;
          };
          const up = (ev) => {
            removeEventListener('pointermove', move);
            removeEventListener('pointerup', up);
            removeEventListener('pointercancel', up);
            this.dragging = false;
            this.classList.remove('ss-dragging');
            if (!moved) return this.classList.remove('ss-moving');
            const kill = (c) => (c.preventDefault(), c.stopPropagation());
            this.addEventListener('click', kill, true);
            setTimeout(() => this.removeEventListener('click', kill, true), 0);
            const dx = ev.clientX - x0;
            // short fast swipe moves one slide, otherwise settle on the nearest slide
            let t = this.near(this.scrollLeft);
            if (performance.now() - t0 < 300 && Math.abs(dx) > 10) {
              t = a0;
              const x = this.pos[a0];
              if (dx < 0) while (t < this.pos.length - 1 && this.pos[t] <= x + 0.5) t++;
              else while (t > 0 && this.pos[t] >= x - 0.5) t--;
            }
            this.go(t);
          };
          addEventListener('pointermove', move, mo);
          addEventListener('pointerup', up, mo);
          addEventListener('pointercancel', up, mo);
        },
        o
      );
    }
  }
  customElements.define('ss-slider', SSSlider);
})();
